import { Filter } from '../filter';
export namespace AI {
    /** AI场景 */
    export enum AIScene {
        /** 流程审批智能分析 */
        FORM_APPROVE_ANALYSIS,

        /** 表单控件赋值 */
        FORM_CONTROL_VALUE_ASSIGN = 1,

        /** AI助手 */
        AI_ASSISTANT = 2,

        /** 表单总结 */
        AI_FORM_SUMMARY = 3,
    }

    /** 消息类型 */
    export enum MessageType {
        /** 用户输入 */
        UserText = 'USER_TEXT',
        /** AI输入 */
        AI = 'AI',

        // 下述内容暂未使用
        System = 'SYSTEM',
        UserImageUrl = 'USER_IMAGE_URL',
        UserImageContent = 'USER_IMAGE_CONTENT',
        ToolExecutionResult = 'TOOL_EXECUTION_RESULT',
    }

    /** AI智能填单配置 */
    export interface IAIAssignSetting {
        recognitionFields: string[];

        recognitionRule: string;

        exampleText: string;

        exampleFiles: Array<{
            fileId: string;
            fileName: string;
            contentType: string;
            contentLength: number;
            sortKey: number;
            thumbnailUrl: string;
        }>;

        disabled: boolean;
    }

    /** AI智能填单识别类型 */
    export enum AIRecognitionType {
        /** 文本 */
        Text = 0,

        /** 文件 */
        File = 1,
    }

    /** AI智能填单识别Prompt */
    export interface IAIAssignPrompt {
        /** 识别内容 */
        content: string;

        /** 识别类型 */
        contentType: AIRecognitionType;
    }

    /** AI智能填单识别字段类型 */
    export enum AIRecognitionFieldType {
        /** 文本 */
        Text = 1,
        /** 数字 */
        Number = 2,
        /** 日期 */
        Date = 3,
        /** 子表 */
        List = 4,
    }

    /** ai场景类型 */
    export enum AIFeatureType {
        /** AI填单助手 */
        Extraction = 1,
        /** AI语音录入功能 */
        Asr = 2,
        /** AI识别（OCR文字识别） */
        Ocr = 3,
    }

    export namespace IGenerationApp {
        /** ai生成应用进度状态枚举 */
        export enum GenerationStatus {
            /** 待开始 */
            PENDING = 'pending',
            /** 进行中 */
            RUNNING = 'running',
            /** 成功 */
            SUCCESS = 'success',
            /** 失败 */
            FAILED = 'failed',
        }

        export enum GenerationType {
            /** 应用 */
            APP = 'app',
            /** 模块，即表单分组 */
            MODULE = 'module',
            /** 模型，即表单 */
            MODEL = 'model',
            /** 氚云，即安装 */
            H3YUN = 'h3yun',
            /** 报表/仪表盘 */
            REPORT = 'report',
            /** 示例数据 */
            SAMPLE_DATA = 'sample_data',
        }

        export type ProgressTreeNode = {
            /** 文本 */
            name: string;
            /** 状态 */
            status: GenerationStatus;
            /** 类型 */
            type: GenerationType;
            /** 子级 */
            children?: ProgressTreeNode[];
        };

        export type ProgressFlatItem = {
            /** 文本 */
            name: string;
            /** 状态 */
            status: GenerationStatus;
            /** 子级 */
            children?: ProgressTreeNode[];
        };

        export enum IRequirementSubmitTypeEnum {
            /** 初始提交 */
            INITIAL = 'initial',
            /** 补充信息 */
            SUPPLEMENT = 'supplement',
            /** 跳过 */
            SKIP = 'skip',
        }

        export enum IAIGenerationSpecificationStatus {
            COMPLETE = 'COMPLETE',
            INCOMPLETE = 'INCOMPLETE',
            INVALID = 'INVALID',
        }

        export type IAIGenerationSpecificationModel = {
            /** 状态 */
            status: IAIGenerationSpecificationStatus;

            /** 关键问题 */
            followupQuestions?: Array<string>;

            /** 需求规格说明 */
            requirementSpec?: string;

            /** 无效需求内容提示 */
            failureReason?: string;
        };

        export type IAIGenerationProgressModel = {
            /** 任务id */
            taskId: string;

            /** 应用编码 */
            appCode: string;

            /** 当前动态名称 */
            currentStep: string;

            /** 状态 */
            status: GenerationStatus;

            /** 动态步骤 */
            steps: ProgressTreeNode[];
        };
    }

    export namespace IAIGenerationCommon {
        /** 获取AI使用量类型枚举 */
        export enum getUsageType {
            /** 获取应用使用量 */
            APP_GENERATION = 'APP_GENERATION',
            /** 获取表单使用量 */
            FORM_INFERENCE = 'FORM_INFERENCE',
        }
    }

    export namespace IGenerationForm {
        export type IAIGenerationControl = {
            /** 控件编码 */
            code: string;
            /** 控件名称 */
            name: string;
            /** 控件类型 */
            type: string;
            /** 额外配置 */
            extendInfo?: Record<string, any>;
        };

        export enum IErrorCode {
            /** 用量耗尽，无次数 */
            USAGE_EXHAUSTED = 'functionUsage.exceeded',
            /** 表单推断无字段 */
            NO_FIELDS = 'form.inference.noFields',
        }
    }

    export namespace AIAssistant {
        /** AI助手模式 */
        export enum Mode {
            /** 问数模式 */
            AskData = 'askData',
            /** Designer模式 */
            Designer = 'designer',
        }

        export enum OutputFormat {
            Markdown = 'markdown',
            PlainText = 'plainText',
            Card = 'card',
            ToolCallOld = 'toolCallOld',
            ToolCall = 'toolCall',
            Designer = 'designer',
        }

        /** 上下文推荐问题信息 */
        export interface IContextSuggestions {
            greeting: string;
            intro: string;
            suggestions: string[];
        }

        export interface IMarkdownOutput {
            id: string;
            format: OutputFormat.Markdown;
            content: string;
            parsedContent: string;
        }

        export interface IPlainTextOutput {
            id: string;
            format: OutputFormat.PlainText;
            content: string;
        }

        export interface IDesignerOutput {
            id: string;
            format: OutputFormat.Designer;
            content: string;
            hasChangedModel?: boolean;
        }

        export interface ICardOutput {
            id: string;
            format: OutputFormat.Card;
            jsonContent: string;
            data: {
                desc: string;
                title: string;
                content: Array<{ key: string; value: string }>;
                actionDone: boolean;
                doActionText: string;
            };
        }

        export interface IToolCallOutputOld {
            id: string;
            format: OutputFormat.ToolCallOld;
            /** 是过程中的工具调用 */
            isMid: boolean;
            data: {
                toolCode: string;
                displayName: string;
                reasoning: string;
            };
        }

        export interface IToolCallOutput {
            id: string;
            format: OutputFormat.ToolCall;
            source: Array<{ toolCode: string; displayName: string; reasoning: string }>;
            progress: string;
            content: string;
        }

        export type IOutput =
            | IMarkdownOutput
            | IPlainTextOutput
            | ICardOutput
            | IToolCallOutputOld
            | IToolCallOutput
            | IDesignerOutput;

        export namespace AIDesigner {
            export const RESTORE_KEY = 'H3_AI_DESIGNER_ASSISTANT_RESTORE';

            export namespace Response {
                export interface ISessionItem {
                    conversationId: string;
                    appCode: string;
                    modelCode: string;
                    title: string;
                    lastUserQuestion: string;
                    lastAiAnswer: string;
                    roundCount: number;
                    createdTime: string;
                    lastUpdatedTime: string;
                    // 暂时没用，要用时定义枚举
                    lastTaskStatus: string;
                    // 暂时没用，要用时定义枚举
                    agentType: string;
                }

                export interface IMessageItem {
                    id: string;
                    sequence: number;
                    userQuestion: string;
                    aiAnswer: string;
                    createdTime: string;
                    // 以下字段暂未使用
                    taskStatus: string;
                    taskType: string;
                    taskTypeName: string;
                    imageUrls: string[];
                    feedbackType: string;
                    feedbackTime: string;
                    feedbackTags: string[];
                }

                export interface IGetSessions {
                    total: number;
                    data: Array<ISessionItem>;
                }

                export interface IGetMessages extends ISessionItem {
                    rounds: Array<IMessageItem>;
                }
            }
        }

        export namespace Request {}

        export namespace Response {
            export interface IGetSessions {
                id: string;
                title: string;
                createdTime: string;
                lastMessageTime: string;
            }

            export interface IGetMessages {
                id: string;
                conversationId: string;
                type: MessageType;
                toolName: string;
                text: string;
                createdTime: string;
            }
        }
    }

    export namespace FormSummary {
        export interface IAIFormSummarySetting {
            /** 是否启用 */
            disabled: boolean;

            /** 总结要求 */
            userConstraints: string;

            /** 分析字段 */
            analysisFields: string[];
        }
    }

    export namespace FormApprovalAnalysis {
        export enum TriggerMode {
            Auto = 'auto',

            Manual = 'manual',
        }

        export enum OutputRequirement {
            Default = 'system_default',

            Custom = 'custom',
        }

        /** 流程审批分析配置 */
        export interface IConfig {
            disabled: boolean;
            outputRequirement: OutputRequirement;
            customRequirement: string;
            triggerMode: TriggerMode;
        }

        /** ai流程审批分析数据 */
        export interface IPrompt {
            schemaCode: string;
            bizObject: Record<string, unknown>;
            workflowRecords: IPromptWorkflowRecord[];
            comments: IPromptComment[];
            /** 当前节点名称 */
            currentNodeName: string;
            /** 当前审批人 */
            currentApprover: string;
            /** 自定义审批要求 */
            userConstraints: string;
        }

        /** 流程审批数据 */
        export interface IPromptWorkflowRecord {
            /** 节点名称 */
            nodeName: string;
            /** 处理人 */
            handler: string;
            /** 操作 */
            action: string;
            /** 处理时间 */
            handleTime: string;
            /** 意见 */
            opinion: string;
        }

        /** 评论数据 */
        export interface IPromptComment {
            /** 评论人 */
            commenter: string;
            /** 评论时间 */
            commentTime: string;
            /** 评论内容 */
            content: string;
        }
    }

    /** AI识别模板 */
    export namespace AIRecognitionTemplate {
        /** AI识别模板类型 */
        export enum TemplateType {
            /** 全部模板 */
            All = 0,
            /** 系统模板 */
            System = 1,
            /** 自定义模板 */
            Custom = 2,
        }

        /** 样本图片文件类型 */
        export type ISampleImageFile = {
            /** 文件ID */
            fileId: string;
            /** 文件名 */
            fileName: string;
            /** 文件类型 */
            contentType: string;
            /** 文件大小 */
            contentLength: number;
            /** 缩略图 */
            thumbnailUrl: string;
            /** sort */
            sortKey?: number;
            /** 源文件 */
            source?: File;
            /** 存储key */
            storageKey?: string;
            /** 进度 */
            percent?: number;
            /** 上传状态 */
            status?: string;
        };

        /** AI识别模板模型 */
        export type ITemplateModel = {
            /** 模板编码 */
            code?: string;
            /** 模板名称 */
            name: string;
            /** 模板描述 */
            description: string;
            /** 样本图片 */
            sampleImage?: string;
            /** 样本文件 */
            sampleFile?: ISampleImageFile;
            /** 模板类型 */
            type: TemplateType;
            /** 主表 */
            table: {
                /** 字段 */
                fields: ITableTemplateModel[];
            };
            /** 排序 */
            sortIndex: number;
        };

        export type ITableTemplateModel = {
            id?: string;
            /** 字段名称 */
            name: string;
            /** 字段描述 */
            description: string;
            /** 字段类型 */
            type: AIRecognitionFieldType;
            /** 子表 */
            subTable?: {
                fields: ITableTemplateModel[];
            };
        };

        export namespace Request {
            export type ITemplateParams = {
                /** 模板编码 */
                code?: string;
                /** 模板名称 */
                name: string;
                /** 模板描述 */
                description: string;
                /** 样本图片 */
                sampleImage?: string;
                /** 模板类型 */
                type: TemplateType;
                /** 主表 */
                table: {
                    /** 字段 */
                    fields: ITableTemplateModel[];
                };
                /** 排序 */
                sortIndex: number;
            };
        }

        export namespace Response {
            export type ITemplateDetail = {
                code: string;
                name: string;
                description: string;
                sortIndex: number;
                type: TemplateType;
                icon: string;
            };

            export type ITemplateDefinitions = ITemplateModel & {
                sampleImage: ISampleImageFile;
            };
        }
    }

    /** AI知识库 */
    export namespace AIKnowledge {
        export enum IKnowledgeStatus {
            /** 未指定 */
            UNSPECIFIED = 0,
            /** 正常 */
            NORMAL = 1,
            /** 停用 */
            DISABLED = 2,
            /** 解析中 */
            PARSING = 3,
            /** 解析失败 */
            PARSE_FAILED = 4,
        }

        export type IKnowledgeFile = {
            /** 文件ID */
            fileId: string;
            /** 文件名 */
            fileName: string;
            /** 文件类型 */
            contentType: string;
            /** 文件大小 */
            fileSize: number;
            /** 文件大小 */
            contentLength: number;
            /** 下载地址 */
            downloadUrl: string;
            /** 缩略图 */
            thumbnailUrl?: string;
            /** 源文件 */
            source?: File;
            /** 存储key */
            storageKey?: string;
            /** 进度 */
            percent?: number;
            /** 上传状态 */
            status?: string;
        };

        /** 知识库模型 */
        export type IKnowledgeModel = {
            objectId: string;
            /** 编码 */
            tenantCode: string;
            topicCode: string;
            /** 名称 */
            name: string;
            /** 描述 */
            description: string;
            /** 文件 */
            files: IKnowledgeFile[];
            /** 文件数量 */
            fileCount: number;
            /** 文件大小 */
            fileSize: number;
            /** 更新时间 */
            modifiedTime?: string;
            /** 状态 */
            status: IKnowledgeStatus;
        };

        export namespace Response {
            /** 知识库列表 */
            export type IAIKnowledgeList = {
                items: IKnowledgeModel[];
                total: number;
                pageNumber: number;
                pageSize: number;
            };
        }
    }

    /** AI智能体 */
    export namespace AIAgent {
        /** 智能体状态 */
        export enum IAgentStatus {
            /** 未添加/停用 */
            Disabled = 0,
            /** 已添加/启用 */
            Enabled = 1,
        }

        /** 智能体模型 */
        export type IAgentModel = {
            /** 编码 */
            id: string;
            /** 名称 */
            name: string;
            /** 描述 */
            description: string;
            /** 类型标签 */
            type: string;
            /** 缩略图 */
            thumbnail: string;
            /** 是否启用 */
            enabled: boolean;
            /** 用户是否可切换 */
            isUserToggleable: boolean;
            /** 技能列表 */
            skills: Array<{ id: string; name: string }>;
            /** 关联的知识库 */
            knowledgeIds: string[];
            knowledgeNames: string[];
        };
    }

    export namespace Request {
        export type IGetAIGenerationForm = {
            /** 业务需求描述 */
            businessDescription: string;
            /** 是否生成表单名称 */
            generateName: boolean;
            /** 已有控件列表 */
            existingFields: IGenerationForm.IAIGenerationControl[] | null;
        };

        export type IAIGenerationSummary = {
            /** 表单编码 */
            schemaCode: string;
            /** 操作类型 */
            actionType: 'summarization' | 'classification';
            /** 引用字段及其字段值 */
            bizObject: Record<string, unknown>;
            /** 结果字段 */
            resultField: string;
            /** 自定义要求 */
            customRequirements: string;
            /** 参考示例 */
            referenceExample: string;
        };

        /**
         * 执行调用agent请求参数模型
         */
        export type IExecuteAgentModel = {
            schemaCode: string;
            controlCode: string;
            valueTable: Record<string, unknown>; // 执行上下文字段值信息
            agentCode: string;
            skillCode: string;
            prompt: string;
            output: string;
            input: object;
            example?: string;
        };
    }

    export namespace Response {
        export type IAIRecognitionOcrExtraction = {
            name: string;
            value: string;
            children?: IAIRecognitionOcrExtraction[][];
        };

        /** 使用日志模型 */
        export type IFeatureUsageLogModel = {
            /** 任务id */
            objectId: string;
            /** 引擎编码 */
            engineCode: string;
            /** 表单编码 */
            schemaCode: string;
            /** 表单名称 */
            schemaName: string;
            /** 应用名称 */
            appName: string;
            /** 功能type */
            featureCode: AIFeatureType;
            /** 操作人ID */
            userId: string;
            /** 操作人名称 */
            userName: string;
            /** 操作时间 */
            logTime: string;
            /** 创建时间 */
            createdTime: string;
            /** 消耗次数 */
            creditUsed: number;
        };

        /** 使用日志模型(月统计) */
        export type IMonthlyFeatureUsageLogModel = {
            /** 月份 */
            statMonth: string;
            /** 调用次数 */
            totalCreditUsed: number;
        };

        export type IFeatureRecordModel = {
            /** 获得来源 */
            type: string;
            /** 公司名称 */
            companyName: string;
            /** 购买时间 */
            drpPaidTime: string;
            /** OCR识别次数 */
            ocrCount: number;
            /** 创建时间 */
            createdTime: string;
        };

        /** 启用清单模型 */
        export type IAIEnableModel = {
            /** 应用编码 */
            appCode: string;
            /** 应用名称 */
            appName: string;
            /** 表单编码 */
            schemaCode: string;
            /** 表单名称 */
            schemaName: string;
            /** 控件清单 */
            controlList: Array<{ controlCode: string; controlName: string }>;
        };

        export type IGetAIGenerationSpecification = IGenerationApp.IAIGenerationSpecificationModel;

        /** 开始AI生成 */
        export type IStartAIGeneration = {
            /** 生成任务id */
            taskId: string;
        };

        /** AI生成进度模型 */
        export type IGetAIGenerationProgress = IGenerationApp.IAIGenerationProgressModel;

        /** 企业当天可用ai生成应用次数 */
        export type IGetAIRemainingCredit = {
            /** 已使用量 */
            usedAmount: number;

            /** 使用上限 */
            usageLimit: number;
        };

        /** AI生成表单 */
        export type IGetAIGenerationForm = {
            /** 表单名称 */
            name: string;
            /** 字段列表 */
            fields: IGenerationForm.IAIGenerationControl[];
        };
    }

    export namespace Agent {
        /**
         * 内部Agent清单
         */
        export enum InternalAgentCode {
            /** 数据分析助手 */
            H3_AGENT_DATA_ANALYST = 'H3_AGENT_DATA_ANALYST',
            /** 表单填充助手 */
            H3_AGENT_FORM_FILL_ASSISTANT = 'H3_AGENT_FORM_FILL_ASSISTANT',
            /** 文本分析助手 */
            H3_AGENT_TEXT_ANALYST = 'H3_AGENT_TEXT_ANALYST',
            /** 识别助手 */
            H3_AGENT_RECOGNITION_ASSISTANT = 'H3_AGENT_RECOGNITION_ASSISTANT',
            /** 报告生成助手 */
            H3_AGENT_REPORT_GENERATE_EXPERT = 'H3_AGENT_REPORT_GENERATE_EXPERT',
            /** 附件解析助手 */
            H3_AGENT_ATTACHMENT_PARSER = 'H3_AGENT_ATTACHMENT_PARSER',
        }

        /**
         * 内部skill清单
         */
        export enum InternalAgentSkillCode {
            /** 总结 */
            H3_AGENT_TEXT_ANALYST_SUMMARIZATION = 'summarization',
            /** 分类 */
            H3_AGENT_TEXT_ANALYST_CLASSIFICATION = 'classification',
            /** 翻译 */
            H3_AGENT_TEXT_ANALYST_TRANSLATION = 'translation',
            /** 润色 */
            H3_AGENT_TEXT_ANALYST_REFINEMENT = 'refinement',
            /** 提取 */
            H3_AGENT_TEXT_ANALYST_EXTRACTION = 'extraction',
            /** 报告生成 */
            H3_AGENT_REPORT_GENERATE_EXPERT_REPORT_GENERATION = 'report-generation',
            /** 分析建议 */
            H3_AGENT_REPORT_GENERATE_EXPERT_ANALYSIS_SUGGESTION = 'analysis-suggestion',
            /** 文件识别 */
            H3_AGENT_ATTACHMENT_PARSER_FILE_RECOGNITION = 'file-recognition',
            /** 合同识别 */
            H3_AGENT_ATTACHMENT_PARSER_CONTRACT_RECOGNITION = 'contract-recognition',
            /** 简历解析 */
            H3_AGENT_ATTACHMENT_PARSER_RESUME_PARSING = 'resume-parsing',
        }

        /**
         * 内置Agent名称映射表
         */
        export const InternalAgentSkillNameMap: Record<InternalAgentSkillCode, string> = {
            [InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_SUMMARIZATION]: '文本总结',
            [InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_CLASSIFICATION]: '文本分类',
            [InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_TRANSLATION]: '文本翻译',
            [InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_REFINEMENT]: '文本润色',
            [InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_EXTRACTION]: '文本提取',
            [InternalAgentSkillCode.H3_AGENT_REPORT_GENERATE_EXPERT_REPORT_GENERATION]: '报告生成',
            [InternalAgentSkillCode.H3_AGENT_REPORT_GENERATE_EXPERT_ANALYSIS_SUGGESTION]: '分析建议',
            [InternalAgentSkillCode.H3_AGENT_ATTACHMENT_PARSER_FILE_RECOGNITION]: '文件识别',
            [InternalAgentSkillCode.H3_AGENT_ATTACHMENT_PARSER_CONTRACT_RECOGNITION]: '合同识别',
            [InternalAgentSkillCode.H3_AGENT_ATTACHMENT_PARSER_RESUME_PARSING]: '简历解析',
        };

        /**
         * 智能体schema定义
         */
        export interface InternalAgentSchema {
            code: string;
            displayName: string;
            description: string;
            icon?: string;
            skills: Array<{
                code: string;
                label: string;
            }>;
            rags?: string[];
        }

        /**
         * 内部Agent清单及其schema定义
         */
        export const InternalAgentSchemaMap: Record<string, InternalAgentSchema> = {
            [InternalAgentCode.H3_AGENT_DATA_ANALYST]: {
                code: InternalAgentCode.H3_AGENT_DATA_ANALYST,
                displayName: '智能数据分析师',
                description:
                    '提供业务数据的多维归因分析能力。支持用户通过自然语言与数据交互，快速完成跨维度指标查询及异常分析。',
                skills: [],
            },
            [InternalAgentCode.H3_AGENT_FORM_FILL_ASSISTANT]: {
                code: InternalAgentCode.H3_AGENT_FORM_FILL_ASSISTANT,
                displayName: '智能填单助手',
                description:
                    '具备多模态的自动识别与填单能力。支持根据用户输入的文本、语音、图片及文件自动提取关键信息，辅助用户快速完成表单填写。',
                skills: [],
            },
            [InternalAgentCode.H3_AGENT_TEXT_ANALYST]: {
                code: InternalAgentCode.H3_AGENT_TEXT_ANALYST,
                displayName: '智能文本分析专家',
                icon: 'https://static.h3yun.com/common-resource/pc/form-designer/images/text-analysis.png',
                description:
                    '提供基于深度语义理解的综合文本处理能力。支持对文本内容进行多维逻辑分级及核心观点提炼。实现文本信息的高效洞察与利用。',
                skills: [
                    {
                        code: InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_SUMMARIZATION,
                        label: '总结',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_CLASSIFICATION,
                        label: '分类',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_TRANSLATION,
                        label: '翻译',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_REFINEMENT,
                        label: '润色',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_TEXT_ANALYST_EXTRACTION,
                        label: '信息提取',
                    },
                ],
            },
            [InternalAgentCode.H3_AGENT_RECOGNITION_ASSISTANT]: {
                code: InternalAgentCode.H3_AGENT_RECOGNITION_ASSISTANT,
                displayName: 'AI识别助手',
                description:
                    '具备高精度的OCR文字识别与图像理解能力。支持快速提取证件、发票、合同扫描件中的关键字段，将非结构化信息转化为结构化数据，提升办公效率。',
                skills: [],
            },
            [InternalAgentCode.H3_AGENT_REPORT_GENERATE_EXPERT]: {
                code: InternalAgentCode.H3_AGENT_REPORT_GENERATE_EXPERT,
                displayName: '分析报告专家',
                description:
                    '自动分析工单问题，结合反馈与知识库自动定位问题源头，并给出合理、可落地的工单建议，快速辅助用户完成工单处理。',
                icon: 'https://static.h3yun.com/common-resource/pc/form-designer/images/report-generate.png',
                skills: [
                    {
                        code: InternalAgentSkillCode.H3_AGENT_REPORT_GENERATE_EXPERT_REPORT_GENERATION,
                        label: '报告生成',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_REPORT_GENERATE_EXPERT_ANALYSIS_SUGGESTION,
                        label: '分析建议',
                    },
                ],
            },
            [InternalAgentCode.H3_AGENT_ATTACHMENT_PARSER]: {
                code: InternalAgentCode.H3_AGENT_ATTACHMENT_PARSER,
                displayName: '附件解析助手',
                description:
                    '基于多模态理解的附件内容解析能力。支持文档、表格、图片等多类型文件的要点提炼与结构化抽取，适用于合同审阅、简历审核、会议纪要等场景。',
                icon: 'https://static.h3yun.com/common-resource/pc/form-designer/images/attachment-parser.png',
                skills: [
                    {
                        code: InternalAgentSkillCode.H3_AGENT_ATTACHMENT_PARSER_FILE_RECOGNITION,
                        label: '文件识别',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_ATTACHMENT_PARSER_CONTRACT_RECOGNITION,
                        label: '合同识别',
                    },
                    {
                        code: InternalAgentSkillCode.H3_AGENT_ATTACHMENT_PARSER_RESUME_PARSING,
                        label: '简历解析',
                    },
                ],
            },
        };

        export type IJoinOn = {
            SourcePropertyName: string;
            TargetPropertyName: string;
        };

        // 关联关系项
        export type RelationItem = {
            Id: string;
            TargetSchemaCode: string | undefined;
            TargetChildSchemaCode: string | undefined;
            JoinOns: IJoinOn[]; // 关联字段映射，0个或1个，第一条记录是0个
        };

        /**
         * 报告生成类智能体配置-数据源
         */
        export type AIReportSource = {
            name: string; // 数据源名称
            relations: RelationItem[]; // 关联关系列表
            filter: Filter.IFilterItem[][]; // 过滤条件列表
            fields: string[]; // 分析字段列表
        };
    }
}
